import { useMemo } from 'react';
import { regionDisplayName } from '../../src/index';
import { useAdmin } from '../session';
import { useT } from './provider';
import { useVocab } from './vocab';

/** A registered market id (`cc:slug`) as stored on a grant, an export record or a ledger row. */
const MARKET_ID = /^[a-z]{2}:[a-z0-9]+(?:-[a-z0-9]+)*$/;

/** A grant / request scope as stored: a type (`GLOBAL`, `COUNTRY`, `CITY`…) and, but for the global one, the id it is scoped to. */
export interface ScopeLike {
  readonly scopeType: string;
  readonly scopeId?: string | undefined;
}

/**
 * D577: the words for a scope — "Plataforma inteira", "Brasil", "Minas Gerais", "Paracatu" — never `GLOBAL` / `BR` / `BR-MG` /
 * `br:paracatu`. A country is named by the platform's region names, a state (`REGION`, ADR-0124) by the region catalog (`Intl.DisplayNames`
 * is not reliable for subdivisions), a city by the registered market catalog; any other scope reads as its type
 * ("Loja", "Frota") followed by the entity it is scoped to.
 */
export function useScopeName(): (scope: ScopeLike) => string {
  const { lang } = useT();
  const { v } = useVocab();
  const { markets, regions: stateCatalog } = useAdmin();
  return useMemo(() => {
    const regions = (() => {
      try {
        return new Intl.DisplayNames([lang], { type: 'region' });
      } catch {
        return undefined;
      }
    })();
    return (scope) => {
      const id = scope.scopeId?.trim() ?? '';
      const type = scope.scopeType.toUpperCase();
      if (type === 'GLOBAL' || id === '') return v('rbac.scope_type', type);
      if (type === 'COUNTRY') {
        const name = regions?.of(id.toUpperCase());
        return name !== undefined && name !== id.toUpperCase() ? name : v('rbac.scope_type', type);
      }
      if (type === 'REGION') return regionDisplayName(id, stateCatalog ?? []);
      const market = markets.find((m) => m.id === id);
      if (market !== undefined) return market.name;
      // A city the catalog does not list reads as "City" — never as its id; a merchant / fleet / partner scope names its entity.
      return type === 'CITY' || MARKET_ID.test(id) ? v('rbac.scope_type', 'CITY') : `${v('rbac.scope_type', type)} · ${id}`;
    };
  }, [lang, markets, stateCatalog, v]);
}

/**
 * D577: the name of a country (`BR` → "Brasil") and of a market (`br:paracatu` → "Paracatu") for a screen that holds only
 * their ids. A market the catalog does not list reads as "City" — never as its id; a plain city name stays as it is.
 */
export function usePlaceNames(): {
  readonly country: (code: string | null | undefined) => string;
  /** A state by its ISO 3166-2 code (`BR-MG` → "Minas Gerais"): the region catalog's name, else the code's suffix (`MG`). */
  readonly region: (code: string | null | undefined) => string;
  readonly market: (id: string | null | undefined) => string;
} {
  const { lang } = useT();
  const { v } = useVocab();
  const { markets, regions: stateCatalog } = useAdmin();
  return useMemo(() => {
    const regions = (() => {
      try {
        return new Intl.DisplayNames([lang], { type: 'region' });
      } catch {
        return undefined;
      }
    })();
    return {
      region: (code) => (code === null || code === undefined || code === '' ? '—' : regionDisplayName(code, stateCatalog ?? [])),
      country: (code) => {
        if (code === null || code === undefined || code === '') return '—';
        const name = regions?.of(code.toUpperCase());
        return name !== undefined && name !== code.toUpperCase() ? name : v('rbac.scope_type', 'COUNTRY');
      },
      market: (id) => {
        if (id === null || id === undefined || id === '') return '—';
        const market = markets.find((m) => m.id === id);
        if (market !== undefined) return market.name;
        return MARKET_ID.test(id) ? v('rbac.scope_type', 'CITY') : id;
      },
    };
  }, [lang, markets, stateCatalog, v]);
}
